import React, { useEffect, useState } from "react";
import SimpleBar from "simplebar-react";
import { useDispatch, useSelector } from "react-redux";
import { convert as htmlToText } from "html-to-text";
import { Search, X, MessageSquareOff } from "lucide-react";

import { getChat } from "slices/chat/thunk";
import { transformDateTime } from "helpers/utils/transformDatetime";
import avatar from "assets/images/users/user-2.jpg";

import { Input } from "components/ui/input";
import { Badge } from "components/ui/badge";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "components/ui/avatar";
import { Skeleton } from "components/ui/skeleton";
import { cn } from "lib/utils";
import { StatusMessage } from "./types";

interface ChatListProps {
  onChatSelect: (chat: any) => void;
  selectedChatId: string | null;
}

const initials = (name?: string) =>
  (name ?? "User")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase())
    .join("")
    .slice(0, 2);

const ChatList: React.FC<ChatListProps> = ({ onChatSelect, selectedChatId }) => {
  const dispatch = useDispatch<any>();
  const allChats = useSelector((state: any) => state.Chat.chats);
  const [searchText, setSearchText] = useState("");
  const [loadingChats, setLoadingChats] = useState(false);

  useEffect(() => {
    if (!allChats || allChats.length === 0) {
      setLoadingChats(true);
      dispatch(getChat()).finally(() => setLoadingChats(false));
    }
  }, [dispatch]);

  const filteredData = Array.isArray(allChats)
    ? allChats.filter((item: any) => {
        const searchMatch =
          !searchText ||
          item.name?.toLowerCase().includes(searchText.toLowerCase());
        const hasMessages =
          item.messages.length > 0 || !!item.lastMessageContent;
        return searchMatch && hasMessages;
      })
    : [];

  const convertHtmlToText = (html: string) =>
    htmlToText(html, {
      wordwrap: false,
      selectors: [
        { selector: "a", options: { ignoreHref: true } },
        { selector: "img", format: "skip" },
      ],
    });

  return (
    <div className="flex h-full flex-col bg-card">
      {/* Header */}
      <div className="space-y-3 border-b border-border/60 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold tracking-tight text-foreground">
            Chats
          </h2>
          {Array.isArray(allChats) && allChats.length > 0 && (
            <Badge variant="soft">{allChats.length}</Badge>
          )}
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by name"
            autoComplete="off"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="h-9 pl-8 pr-8 text-sm"
          />
          {searchText && (
            <button
              type="button"
              onClick={() => setSearchText("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* List */}
      {loadingChats ? (
        <div className="flex-1 space-y-3 p-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-9 w-9 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-2/3" />
                <Skeleton className="h-3 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <SimpleBar className="max-h-[calc(100vh-200px)] flex-1 overflow-y-auto">
          {filteredData.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center px-4 text-center">
              <MessageSquareOff className="mb-2 h-8 w-8 text-muted-foreground/60" />
              <p className="text-sm font-medium text-foreground">
                No conversations
              </p>
              <p className="text-xs text-muted-foreground">
                Try a different search keyword.
              </p>
            </div>
          ) : (
            <ul className="flex flex-col gap-0.5 px-2 py-2" id="chatList">
              <li className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                Recent Chats
              </li>
              {filteredData.map((item: any, key: number) => {
                const isActive = selectedChatId === item.id;
                const isUnread =
                  item.statusMessage === StatusMessage.UnRead ||
                  item?.unreadMessageCount > 0;

                return (
                  <React.Fragment key={item.id ?? key}>
                    {key === 3 && (
                      <li className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                        All Conversations
                      </li>
                    )}
                    <li>
                      <button
                        type="button"
                        onClick={() => onChatSelect(item)}
                        className={cn(
                          "group relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
                          isActive
                            ? "bg-primary/10"
                            : "hover:bg-muted/60"
                        )}
                      >
                        {isActive && (
                          <span
                            aria-hidden
                            className="absolute inset-y-1.5 left-0 w-1 rounded-r-full bg-gradient-to-b from-primary to-accent"
                          />
                        )}

                        <div className="relative shrink-0">
                          <Avatar className="h-9 w-9">
                            {item?.image ? (
                              <AvatarImage src={avatar} alt={item.name} />
                            ) : null}
                            <AvatarFallback>
                              {initials(item.name)}
                            </AvatarFallback>
                          </Avatar>
                          {isUnread && (
                            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-semibold text-destructive-foreground ring-2 ring-card">
                              {item?.unreadMessageCount || ""}
                            </span>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p
                              className={cn(
                                "truncate text-sm",
                                isUnread || isActive
                                  ? "font-semibold text-foreground"
                                  : "font-medium text-foreground"
                              )}
                            >
                              {item.name ?? "Unnamed Contact"}
                            </p>
                            {item.lastMessageDateTime && (
                              <span
                                className={cn(
                                  "shrink-0 text-[11px]",
                                  isUnread
                                    ? "font-semibold text-primary"
                                    : "text-muted-foreground"
                                )}
                              >
                                {transformDateTime(item.lastMessageDateTime)}
                              </span>
                            )}
                          </div>
                          <p
                            className={cn(
                              "mt-0.5 truncate text-xs",
                              isUnread
                                ? "font-semibold text-foreground/90"
                                : "text-muted-foreground"
                            )}
                          >
                            {item.lastMessageContent
                              ? convertHtmlToText(item.lastMessageContent)
                              : item.designation ?? "No messages yet"}
                          </p>
                        </div>
                      </button>
                    </li>
                  </React.Fragment>
                );
              })}
            </ul>
          )}
        </SimpleBar>
      )}
    </div>
  );
};

export default ChatList;
